import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAuth } from '../contexts/AuthContext';
import { useGuardian } from '../contexts/GuardianContext';
import supabase from '../supabase/supabase';

const { FiHeart, FiMail, FiPhone, FiMapPin, FiPlus, FiAlertCircle, FiExternalLink, FiStar } = FiIcons;

function CollegeTracker() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getActiveUserId } = useGuardian();
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadSavedSchools();
    }
    const handleSchoolsUpdated = () => {
      loadSavedSchools();
    };
    window.addEventListener('schoolsUpdated', handleSchoolsUpdated);
    return () => window.removeEventListener('schoolsUpdated', handleSchoolsUpdated);
  }, [user, getActiveUserId()]);

  const loadSavedSchools = async () => {
    try {
      setLoading(true);
      const activeUserId = getActiveUserId();

      const { data: savedData, error: savedError } = await supabase
        .from('saved_schools')
        .select('*')
        .eq('user_id', activeUserId);

      if (savedError) throw savedError;

      if (!savedData || savedData.length === 0) {
        setColleges([]);
        setLoading(false);
        return;
      }

      const featuredIds = savedData.filter(s => s.school_type === 'featured').map(s => s.school_id);
      const customIds = savedData.filter(s => s.school_type === 'custom').map(s => s.school_id);

      let allSchools = [];

      if (featuredIds.length > 0) {
        const { data: featuredSchools, error: featuredError } = await supabase
          .from('featured_schools')
          .select('*')
          .in('id', featuredIds);
        if (featuredError) throw featuredError;

        allSchools = [
          ...allSchools,
          ...(featuredSchools || []).map(school => {
            const savedInfo = savedData.find(s => s.school_type === 'featured' && s.school_id === school.id);
            return {
              ...school,
              school_type: 'featured',
              saved_id: savedInfo.id,
              interest_level: savedInfo.interest_level,
              status: savedInfo.status,
              notes: savedInfo.notes,
              last_contact: savedInfo.last_contact
            };
          })
        ];
      }

      if (customIds.length > 0) {
        const { data: customSchools, error: customError } = await supabase
          .from('user_schools')
          .select('*')
          .in('id', customIds);
        if (customError) throw customError;

        allSchools = [
          ...allSchools,
          ...(customSchools || []).map(school => {
            const savedInfo = savedData.find(s => s.school_type === 'custom' && s.school_id === school.id);
            return {
              ...school,
              school_type: 'custom',
              saved_id: savedInfo.id,
              interest_level: savedInfo.interest_level,
              status: savedInfo.status,
              notes: savedInfo.notes,
              last_contact: savedInfo.last_contact
            };
          })
        ];
      }

      setColleges(allSchools);
    } catch (error) {
      console.error('Error loading saved schools:', error);
    } finally {
      setLoading(false);
    }
  };

  const getInterestColor = (level) => {
    switch (level) {
      case 'high': return 'bg-green-100 text-green-800 border-green-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'interested': return 'bg-green-100 text-green-800';
      case 'visiting': return 'bg-blue-100 text-blue-800';
      case 'active': return 'bg-purple-100 text-purple-800';
      case 'contacted': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleRemoveFavorite = async (college) => {
    if (!window.confirm('Are you sure you want to remove this school from your favorites?')) {
      return;
    }
    try {
      const { error } = await supabase.from('saved_schools').delete().eq('id', college.saved_id);
      if (error) throw error;
      setColleges(colleges.filter(c => c.saved_id !== college.saved_id));
      window.dispatchEvent(new Event('schoolsUpdated'));
    } catch (error) {
      console.error('Error removing favorite:', error);
      alert('Failed to remove school from favorites');
    }
  };

  const handleInterestLevelChange = async (college, newLevel) => {
    try {
      const { error } = await supabase.from('saved_schools').update({ interest_level: newLevel }).eq('id', college.saved_id);
      if (error) throw error;
      setColleges(colleges.map(c => c.saved_id === college.saved_id ? { ...c, interest_level: newLevel } : c));
    } catch (error) {
      console.error('Error updating interest level:', error);
      alert('Failed to update interest level');
    }
  };

  const handleStatusChange = async (college, newStatus) => {
    try {
      const { error } = await supabase.from('saved_schools').update({ status: newStatus }).eq('id', college.saved_id);
      if (error) throw error;
      setColleges(colleges.map(c => c.saved_id === college.saved_id ? { ...c, status: newStatus } : c));
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    }
  };

  const savedCount = colleges.length;
  const highInterestCount = colleges.filter(c => c.interest_level === 'high').length;
  const visitingCount = colleges.filter(c => c.status === 'visiting').length;
  const activeCount = colleges.filter(c => c.status === 'active').length;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading saved schools...</p>
          </div>
        </div>
      </div>
    );
  }

  if (colleges.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-space-grotesk font-semibold text-secondary">College Tracking Board</h2>
            <p className="text-gray-600 mt-1">Manage your relationships with college coaches and programs</p>
          </div>
        </div>
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-100 text-center">
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiHeart} className="w-8 h-8 text-primary" />
            </div>
            <h3 className="text-xl font-space-grotesk font-semibold text-secondary mb-2">No Saved Schools Yet</h3>
            <p className="text-gray-600 mb-6">Start building your college list by saving schools from the School Directory.</p>
            <button onClick={() => navigate('/schools')} className="inline-flex items-center space-x-2 bg-primary text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors btn-primary">
              <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
              <span>Browse School Directory</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-space-grotesk font-semibold text-secondary">College Tracking Board</h2>
          <p className="text-gray-600 mt-1">Manage your relationships with college coaches and programs</p>
        </div>
        <div className="flex space-x-3">
          <button onClick={() => navigate('/schools')} className="bg-white border-2 border-primary text-primary px-4 py-2 rounded-lg hover:bg-blue-50 transition-colors flex items-center space-x-2">
            <SafeIcon icon={FiPlus} className="w-4 h-4" />
            <span>Add Schools</span>
          </button>
          <button onClick={() => navigate('/communications')} className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors btn-primary flex items-center space-x-2">
            <SafeIcon icon={FiMail} className="w-4 h-4" />
            <span>Outreach CRM</span>
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
          <div className="text-2xl font-bold text-primary">{savedCount}</div>
          <div className="text-sm text-gray-600">Schools Tracked</div>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
          <div className="text-2xl font-bold text-green-600">{highInterestCount}</div>
          <div className="text-sm text-gray-600">High Interest</div>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
          <div className="text-2xl font-bold text-blue-600">{visitingCount}</div>
          <div className="text-sm text-gray-600">Campus Visits</div>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
          <div className="text-2xl font-bold text-purple-600">{activeCount}</div>
          <div className="text-sm text-gray-600">Active Discussions</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {colleges.map((college, index) => (
          <motion.div
            key={college.saved_id}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start space-x-4 mb-4">
              <img src={college.logo} alt={college.name} className="w-12 h-12 rounded-lg object-cover" />
              <div className="flex-1">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="text-lg font-space-grotesk font-semibold text-secondary">{college.name}</h3>
                    <div className="flex items-center space-x-1 text-sm text-gray-600">
                      <SafeIcon icon={FiMapPin} className="w-3 h-3" />
                      <span>{college.location}</span>
                    </div>
                  </div>
                  <button onClick={() => handleRemoveFavorite(college)} className="text-red-500 hover:text-red-600 transition-colors" title="Remove from favorites">
                    <SafeIcon icon={FiHeart} className="w-5 h-5 fill-current" />
                  </button>
                </div>
                
                <div className="text-sm text-gray-600 mb-3">
                  <strong>Coach:</strong> {college.coach}
                </div>

                <div className="flex items-center space-x-2 mb-3">
                  <div className="relative">
                    <select
                      value={college.interest_level}
                      onChange={(e) => handleInterestLevelChange(college, e.target.value)}
                      className={`px-3 py-1.5 text-xs rounded-full capitalize border cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary appearance-none pr-8 ${getInterestColor(college.interest_level)}`}
                      style={{ minWidth: '90px' }}
                    >
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                    <SafeIcon icon={FiStar} className="w-3 h-3 absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none" />
                  </div>
                  {college.status && (
                    <div className="relative">
                      <select
                        value={college.status}
                        onChange={(e) => handleStatusChange(college, e.target.value)}
                        className={`px-3 py-1.5 text-xs rounded-full capitalize cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary appearance-none pr-8 ${getStatusColor(college.status)}`}
                        style={{ minWidth: '110px' }}
                      >
                        <option value="pending">Pending</option>
                        <option value="contacted">Contacted</option>
                        <option value="interested">Interested</option>
                        <option value="visiting">Visiting</option>
                        <option value="active">Active</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {(college.email || college.phone) && (
              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <div className="grid grid-cols-2 gap-3 text-xs text-gray-600">
                  {college.email && (
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiMail} className="w-3 h-3" />
                      <span className="truncate">{college.email}</span>
                    </div>
                  )}
                  {college.phone && (
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiPhone} className="w-3 h-3" />
                      <span>{college.phone}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="flex items-center space-x-3 mt-4 pt-4 border-t border-gray-100">
              <button onClick={() => navigate('/communications')} className="flex-1 bg-primary text-white py-2 px-3 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium text-center flex justify-center items-center space-x-2">
                <SafeIcon icon={FiMail} className="w-4 h-4" />
                <span>Log Outreach</span>
              </button>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

export default CollegeTracker;